import { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Select, Switch, Space, Tag, Popconfirm, message, Spin, Divider, Alert } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, BranchesOutlined, LinkOutlined } from "@ant-design/icons";
import Header from "../_shared/components/Header";
import TaskConditions from "./TaskConditions";
import TaskStatusMappings from "./TaskStatusMappings";
import {
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetStaffProfilesQuery,
  useGetSchedulingMachinesQuery,
  useGetMachineGroupsQuery,
} from "../../../redux/api/scheduling";

const STAFF_CAPACITY_METHODS = [
  { value: "none", label: "None" },
  { value: "slot", label: "Slot" },
  { value: "time", label: "Time" },
];

const MACHINE_METHODS = [
  { value: "none", label: "None" },
  { value: "time", label: "Time" },
];

const BATCH_METHODS = [
  { value: "quality_interval", label: "Quality Interval + Minimum Batch" },
  { value: "fixed_count", label: "Number of Batches" },
  { value: "batch_size", label: "Batch Size" },
];

const OPERATING_WINDOWS = [
  { value: "anytime", label: "Anytime - 24/7" },
  { value: "weekdays", label: "Weekdays - All Day" },
  { value: "weekdays_biz", label: "Weekdays - Business Hours" },
  { value: "non_holiday_weekdays", label: "Non-Holiday Weekdays - All Day" },
  { value: "non_holiday_weekdays_biz", label: "Non-Holiday Weekdays - Business Hours" },
];

const PROFILE_COLORS: Record<string, string> = {
  OP: "blue",
  US: "green",
  OS: "purple",
  SM: "orange",
  RD: "cyan",
  AD: "magenta",
};

export default function Tasks() {
  const { data, isLoading } = useGetTasksQuery({});
  const { data: profilesData } = useGetStaffProfilesQuery({});
  const { data: machinesData } = useGetSchedulingMachinesQuery({});
  const { data: machineGroupsData } = useGetMachineGroupsQuery({});
  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedConditionTask, setSelectedConditionTask] = useState<{ id: number; name: string } | null>(null);
  const [selectedMappingTask, setSelectedMappingTask] = useState<{ id: number; name: string } | null>(null);
  const [form] = Form.useForm();
  const isBatchTask = Form.useWatch("is_batch_task", form);
  const batchMethod = Form.useWatch("batch_method", form);
  const watchMachineMethod = Form.useWatch("machine_method", form);
  const watchMachineIds = Form.useWatch("machine_ids", form);
  const watchMachineGroupIds = Form.useWatch("machine_group_ids", form);

  const tasks = data?.results || data || [];
  const profileList = profilesData?.results || profilesData || [];
  const machineList = machinesData?.results || machinesData || [];
  const machineGroupList = machineGroupsData?.results || machineGroupsData || [];

  // Build a lookup map: profile id → {name, initial}
  const profileMap: Record<number, { name: string; initial: string }> = {};
  profileList.forEach((p: any) => {
    profileMap[p.id] = { name: p.name, initial: p.initial };
  });

  // Fetch the full detail when editing (has all M2M fields)
  const { data: editTaskDetail, isFetching: isFetchingDetail } = useGetTaskQuery(editingId!, {
    skip: editingId === null,
  });

  // Populate form when detail loads
  useEffect(() => {
    if (editTaskDetail && editingId !== null) {
      form.setFieldsValue({
        task_id: editTaskDetail.task_id,
        name: editTaskDetail.name,
        definition: editTaskDetail.definition,
        staff_capacity_method: editTaskDetail.staff_capacity_method,
        machine_method: editTaskDetail.machine_method,
        is_multi_machine: editTaskDetail.is_multi_machine,
        operating_window: editTaskDetail.operating_window || "anytime",
        is_final_task: editTaskDetail.is_final_task,
        is_primary: editTaskDetail.is_primary,
        is_batch_task: editTaskDetail.is_batch_task,
        batch_method: editTaskDetail.batch_method || undefined,
        batch_min_accumulation: editTaskDetail.batch_min_accumulation,
        batch_count: editTaskDetail.batch_count,
        batch_size_qty: editTaskDetail.batch_size_qty,
        // M2M fields: the detail serializer returns PK arrays for these
        profile_access_ids: editTaskDetail.profile_access || [],
        default_access_ids: editTaskDetail.default_access || [],
        machine_ids: editTaskDetail.machines || [],
        machine_group_ids: editTaskDetail.machine_groups || [],
        setup_task_ids: editTaskDetail.setup_tasks || [],
        teardown_task_ids: editTaskDetail.teardown_tasks || [],
      });
    }
  }, [editTaskDetail, editingId, form]);

  const handleSubmit = async (values: any) => {
    try {
      if (editingId) {
        await updateTask({ id: editingId, ...values }).unwrap();
        message.success("Task updated");
      } else {
        await createTask(values).unwrap();
        message.success("Task created");
      }
      setModalOpen(false);
      form.resetFields();
      setEditingId(null);
    } catch (err: any) {
      const d = err?.data;
      if (d?.detail) {
        message.error(d.detail);
      } else if (d && typeof d === "object") {
        const msgs = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
        message.error(msgs.join("; ") || "Error saving task");
      } else {
        message.error("Error saving task");
      }
    }
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    form.resetFields();
    setEditingId(null);
  };

  const handleDelete = async (id: number) => {
    await deleteTask(id).unwrap();
    message.success("Task deleted");
  };

  // Resolve profile PKs to tags using either the detail objects from the
  // list endpoint or the lookup map built from the profiles endpoint.
  const renderProfileTags = (record: any, field: string, detailField: string) => {
    // Prefer the nested detail objects returned by the list serializer
    const details: any[] | undefined = record[detailField];
    if (details && details.length > 0) {
      return (
        <div className="flex flex-wrap gap-1">
          {details.map((p: any) => (
            <Tag key={p.id} color={PROFILE_COLORS[p.initial] || "default"}>
              {p.initial}
            </Tag>
          ))}
        </div>
      );
    }

    // Fallback: resolve PK array through the profile lookup map
    const pks: number[] | undefined = record[field];
    if (pks && pks.length > 0) {
      return (
        <div className="flex flex-wrap gap-1">
          {pks.map((pk: number) => {
            const prof = profileMap[pk];
            return (
              <Tag key={pk} color={prof ? PROFILE_COLORS[prof.initial] || "default" : "default"}>
                {prof ? prof.initial : `#${pk}`}
              </Tag>
            );
          })}
        </div>
      );
    }

    return <span className="text-gray-400 text-xs italic">None</span>;
  };

  const columns = [
    { title: "ID", dataIndex: "task_id", key: "task_id", width: 80 },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Profile Access",
      key: "profile_access_col",
      render: (_: any, record: any) =>
        renderProfileTags(record, "profile_access", "profile_access_detail"),
    },
    {
      title: "Default Access",
      key: "default_access_col",
      render: (_: any, record: any) =>
        renderProfileTags(record, "default_access", "default_access_detail"),
    },
    {
      title: "Staff Method",
      dataIndex: "staff_capacity_method",
      key: "staff_capacity_method",
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: "Machine Method",
      dataIndex: "machine_method",
      key: "machine_method",
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: "Final",
      dataIndex: "is_final_task",
      key: "is_final_task",
      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : "No"),
    },
    {
      title: "Primary",
      dataIndex: "is_primary",
      key: "is_primary",
      render: (v: boolean) => (v ? <Tag color="blue">Yes</Tag> : "No"),
    },
    {
      title: "Multi-Machine",
      dataIndex: "is_multi_machine",
      key: "is_multi_machine",
      render: (v: boolean) => (v ? <Tag color="orange">Yes</Tag> : "No"),
    },
    {
      title: "Operating Window",
      dataIndex: "operating_window",
      key: "operating_window",
      render: (v: string) => {
        const label = OPERATING_WINDOWS.find((w) => w.value === v)?.label || v || "Anytime - 24/7";
        return <Tag>{label}</Tag>;
      },
    },
    {
      title: "Batch",
      dataIndex: "is_batch_task",
      key: "is_batch_task",
      render: (v: boolean, record: any) =>
        v ? (
          <Tag color="volcano">
            {BATCH_METHODS.find((m) => m.value === record.batch_method)?.label || "Yes"}
          </Tag>
        ) : (
          "No"
        ),
    },
    {
      title: "Conditions",
      dataIndex: "conditions_count",
      key: "conditions_count",
      width: 90,
      render: (v: number) =>
        v > 0 ? <Tag color="geekblue">{v}</Tag> : <span className="text-gray-400">0</span>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<BranchesOutlined />}
            size="small"
            onClick={() => setSelectedConditionTask({ id: record.id, name: `[${record.task_id}] ${record.name}` })}
          >
            Conditions
          </Button>
          <Button
            icon={<LinkOutlined />}
            size="small"
            onClick={() => setSelectedMappingTask({ id: record.id, name: `[${record.task_id}] ${record.name}` })}
          >
            Mappings
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete this task?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const profileOptions = profileList.map((p: any) => ({
    value: p.id,
    label: `${p.name} (${p.initial})`,
  }));

  const machineOptions = machineList.map((m: any) => ({
    value: m.id,
    label: m.name,
  }));

  const machineGroupOptions = machineGroupList.map((g: any) => ({
    value: g.id,
    label: g.name,
  }));

  const taskOptions = tasks.map((t: any) => ({
    value: t.id,
    label: `[${t.task_id}] ${t.name}`,
  }));

  if (selectedMappingTask) {
    return (
      <div className="w-full p-4">
        <Button onClick={() => setSelectedMappingTask(null)} className="mb-4">
          ← Back to Tasks
        </Button>
        <TaskStatusMappings taskId={selectedMappingTask.id} taskName={selectedMappingTask.name} />
      </div>
    );
  }

  if (selectedConditionTask) {
    return (
      <div className="w-full p-4">
        <Button onClick={() => setSelectedConditionTask(null)} className="mb-4">
          ← Back to Tasks
        </Button>
        <TaskConditions taskId={selectedConditionTask.id} taskName={selectedConditionTask.name} />
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <Header
        title="Tasks"
        showOnClickButton
        buttonText="Add Task"
        icon={<PlusOutlined />}
        handleOnClick={() => {
          setEditingId(null);
          form.resetFields();
          setModalOpen(true);
        }}
      />

      <Table
        dataSource={tasks}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 20 }}
      />

      <Modal
        title={editingId ? "Edit Task" : "Create Task"}
        open={modalOpen}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        width={700}
        destroyOnClose
      >
        {editingId && isFetchingDetail ? (
          <div className="flex justify-center py-10">
            <Spin size="large" />
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item name="task_id" label="Task ID" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="name" label="Task Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="definition" label="Definition">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="profile_access_ids" label="Profile Access">
              <Select mode="multiple" options={profileOptions} placeholder="Select profiles..." />
            </Form.Item>
            <Form.Item name="default_access_ids" label="Default Access">
              <Select mode="multiple" options={profileOptions} placeholder="Select profiles..." />
            </Form.Item>
            <Form.Item name="staff_capacity_method" label="Staff Capacity Method">
              <Select options={STAFF_CAPACITY_METHODS} />
            </Form.Item>
            <Form.Item name="machine_ids" label="Machines">
              <Select mode="multiple" options={machineOptions} placeholder="Select individual machines..." />
            </Form.Item>
            <Form.Item name="machine_group_ids" label="Machine Groups" tooltip="Machines in selected groups are also eligible. Groups resolve dynamically.">
              <Select mode="multiple" options={machineGroupOptions} placeholder="Select machine groups..." />
            </Form.Item>
            <Form.Item name="machine_method" label="Machine Method">
              <Select options={MACHINE_METHODS} />
            </Form.Item>
            {watchMachineMethod === "none" && ((watchMachineIds?.length ?? 0) > 0 || (watchMachineGroupIds?.length ?? 0) > 0) && (
              <Alert
                type="warning"
                showIcon
                message="Machines are assigned but Machine Method is 'None'. The scheduler will ignore machine time for this task."
                className="mb-4"
              />
            )}
            {watchMachineMethod === "time" && (watchMachineIds?.length ?? 0) === 0 && (watchMachineGroupIds?.length ?? 0) === 0 && (
              <Alert
                type="warning"
                showIcon
                message="Machine Method is 'Time' but no machines or machine groups are assigned. The scheduler cannot allocate machine time."
                className="mb-4"
              />
            )}
            <Form.Item name="is_final_task" label="Final Task" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="is_primary" label="Primary Task" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              name="is_multi_machine"
              label="Multi-Machine Task"
              valuePropName="checked"
              tooltip="When a job is split, only multi-machine tasks are divided across sub-jobs. Other tasks stay on the first sub-job at full duration."
            >
              <Switch />
            </Form.Item>
            <Form.Item name="operating_window" label="Operating Window" initialValue="anytime">
              <Select options={OPERATING_WINDOWS} />
            </Form.Item>

            <Divider plain>Batch Task Settings</Divider>
            <Form.Item
              name="is_batch_task"
              label="Batch Task"
              valuePropName="checked"
              tooltip="Generates multiple sub-tasks per job based on completion percentage of a linked dependency task."
            >
              <Switch />
            </Form.Item>
            {isBatchTask && (
              <>
                <Form.Item
                  name="batch_method"
                  label="Batch Method"
                  rules={[{ required: true, message: "Select a batch method" }]}
                  tooltip="How the number of sub-tasks is determined."
                >
                  <Select options={BATCH_METHODS} placeholder="Select batch method..." />
                </Form.Item>
                {batchMethod === "quality_interval" && (
                  <Form.Item
                    name="batch_min_accumulation"
                    label="Minimum Batch Accumulation"
                    rules={[{ required: true, message: "Enter minimum accumulation" }]}
                    tooltip="Minimum parts that must accumulate at inspection checkpoints before triggering a batch sub-task."
                  >
                    <InputNumber min={1} className="w-full" />
                  </Form.Item>
                )}
                {batchMethod === "fixed_count" && (
                  <Form.Item
                    name="batch_count"
                    label="Number of Batches"
                    rules={[{ required: true, message: "Enter number of batches" }]}
                    tooltip="Fixed number of sub-tasks, evenly distributed across the linked task's completion."
                  >
                    <InputNumber min={1} className="w-full" />
                  </Form.Item>
                )}
                {batchMethod === "batch_size" && (
                  <Form.Item
                    name="batch_size_qty"
                    label="Parts Per Batch"
                    rules={[{ required: true, message: "Enter batch size" }]}
                    tooltip="Number of parts per batch. Total sub-tasks = ceil(quantity / batch size)."
                  >
                    <InputNumber min={1} className="w-full" />
                  </Form.Item>
                )}
              </>
            )}

            <Divider plain>Setup / Teardown</Divider>
            <Form.Item name="setup_task_ids" label="Setup Tasks">
              <Select mode="multiple" options={taskOptions} placeholder="Select setup tasks..." />
            </Form.Item>
            <Form.Item name="teardown_task_ids" label="Teardown Tasks">
              <Select mode="multiple" options={taskOptions} placeholder="Select teardown tasks..." />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
